// src/Components/Events.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Tabs,
  Tab,
  ImageList,
  ImageListItem,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Place as PlaceIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as GoingIcon,
  Cancel as NotGoingIcon,
  Help as MaybeIcon,
  CalendarMonth as CalendarIcon,
  Comment as CommentIcon,
  PhotoCamera as PhotoIcon,
  People as PeopleIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Celebration as CelebrationIcon,
  Cake as CakeIcon,
  Favorite as HeartIcon,
  CardGiftcard as GiftIcon,
  Home as HomeIcon,
  Restaurant as RestaurantIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  Photo as GalleryIcon,
  LinkedIn as LinkedInIcon,
  Receipt as ReceiptIcon,
  ChildCare as ChildCareIcon,
  Paid as PaidIcon,
  LocalDining as CateringIcon,
  StoreMallDirectory as VenueIcon,
  MusicNote as EntertainmentIcon,
  Category as CategoryIcon,
  Hotel as HotelIcon,
  FileDownload as DownloadIcon,
  Print as PrintIcon,
  Directions as DirectionsIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { format, formatDistanceToNow, isPast, isFuture, parseISO } from 'date-fns';
import { sanitizeImageUrl } from '../utils/urlSanitizer';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import EmojiPicker from './EmojiPicker';
import { notifyTaggedMembers, notifyNewEvent } from '../services/notificationService';
import logger from '../utils/logger';
import LocationPicker, { buildMapsUrl, buildDirectionsUrl } from './LocationPicker';

// Normalise location to object so buildMapsUrl / buildDirectionsUrl work with legacy string values
const normalizeLocation = (loc) => {
  if (!loc) return null;
  if (typeof loc === 'string') return { name: loc, address: loc };
  return loc;
};
const getLocationStr = (loc) => {
  if (!loc) return '';
  if (typeof loc === 'string') return loc;
  return loc.name || loc.address || '';
};

const Events = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'casual', // casual, reunion, birthday, holiday, anniversary
    date: '',
    endDate: '', // For multi-day events like reunions
    time: '',
    location: null,
    // Reunion-specific fields
    theme: '',
    accommodation: '',
    venueLocation: null,
    budget: '', // Legacy simple budget (kept for backward compat)
    budgetDetails: {
      totalBudget: '',
      lineItems: [], // [{ id, category, description, amount }]
      guestPricing: {
        adultPrice: '',
        kidsTiers: [] // [{ id, label, ageMin, ageMax, price }]
      }
    },
    schedule: [],
    // Enhanced meal planning
    diningStyle: '', // 'potluck', 'catered', or '' (not set)
    mealPlan: [],
    catererInfo: { name: '', phone: '', website: '', notes: '' },
    whosBringing: [],
    // Hotel/registration link
    hotelLink: '',
    hotelInfo: '',
    // Photos
    photos: [],
    // Social contact info
    contactPhone: '',
    contactWebsite: '',
    contactInstagram: '',
    contactFacebook: '',
    contactLinkedIn: '',
    contactTwitter: '',
    // Privacy and tagging
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });

  // Event details dialog (comments, photos, attendees)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsTab, setDetailsTab] = useState(0); // 0=Comments, 1=Attendees, 2=Photos
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [eventPhotos, setEventPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const eventPhotoInputRef = useRef(null);
  const formPhotoInputRef = useRef(null);

  const storage = getStorage();

  // Reunion planning states
  const [scheduleItem, setScheduleItem] = useState({ day: '', time: '', activity: '' });
  const [mealItem, setMealItem] = useState({ meal: '', day: '', description: '' });
  const [bringingItem, setBringingItem] = useState({ item: '', assignedTo: '' });
  const [budgetLineItem, setBudgetLineItem] = useState({ category: '', description: '', amount: '' });
  const [kidsTierItem, setKidsTierItem] = useState({ label: '', ageMin: '', ageMax: '', price: '' });

  // RSVP dialog state (for guest count when RSVPing "Going")
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [rsvpEventId, setRsvpEventId] = useState(null);
  const [rsvpGuests, setRsvpGuests] = useState({ adultsCount: 1, kidsCount: 0, kidsByAge: [] });

  const db = getFirestore();

  // Event type configurations
  const eventTypes = [
    { value: 'casual', label: 'Casual Get-Together', icon: <PeopleIcon />, color: '#2196f3' },
    { value: 'reunion', label: 'Family Reunion', icon: <CelebrationIcon />, color: '#f50057' },
    { value: 'birthday', label: 'Birthday Party', icon: <CakeIcon />, color: '#ff9800' },
    { value: 'holiday', label: 'Holiday Gathering', icon: <GiftIcon />, color: '#4caf50' },
    { value: 'anniversary', label: 'Anniversary', icon: <HeartIcon />, color: '#e91e63' }
  ];

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'events'),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const eventsData = [];
        querySnapshot.forEach((doc) => {
          eventsData.push({ id: doc.id, ...doc.data() });
        });
        setEvents(eventsData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching events:', error);
        setError('Failed to load events');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  // Fetch comments when event details dialog opens
  useEffect(() => {
    if (!selectedEvent || !detailsDialogOpen || !familyBoardId) {
      setComments([]);
      return;
    }

    setLoadingComments(true);
    const q = query(
      collection(db, 'families', familyBoardId, 'events', selectedEvent.id, 'comments'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const commentsData = [];
        querySnapshot.forEach((doc) => {
          commentsData.push({ id: doc.id, ...doc.data() });
        });
        setComments(commentsData);
        setLoadingComments(false);
      },
      (error) => {
        logger.error('Error fetching comments:', error);
        setLoadingComments(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId, selectedEvent, detailsDialogOpen]);

  // Fetch event photos when Photos tab is active
  useEffect(() => {
    if (!selectedEvent || !detailsDialogOpen || !familyBoardId || detailsTab !== 2) {
      return;
    }

    setLoadingPhotos(true);
    const q = query(
      collection(db, 'families', familyBoardId, 'events', selectedEvent.id, 'photos'),
      orderBy('uploadedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const photosData = [];
        querySnapshot.forEach((doc) => {
          photosData.push({ id: doc.id, ...doc.data() });
        });
        setEventPhotos(photosData);
        setLoadingPhotos(false);
      },
      (error) => {
        logger.error('Error fetching event photos:', error);
        setLoadingPhotos(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId, selectedEvent, detailsDialogOpen, detailsTab]);

  // Handle photo upload for event creation form
  const handleFormPhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length + formData.photos.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }

    setPhotoUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const storageRef = ref(storage, `families/${familyBoardId}/events/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const photoUrls = await Promise.all(uploadPromises);
      setFormData({ ...formData, photos: [...formData.photos, ...photoUrls] });
    } catch (err) {
      logger.error('Error uploading photos:', err);
      setError('Failed to upload photos');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removeFormPhoto = (index) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
  };

  // Handle photo upload in event details (Photos tab)
  const handleEventPhotoUpload = async (event) => {
    if (!selectedEvent || !familyBoardId) return;
    const files = Array.from(event.target.files);

    setPhotoUploading(true);
    try {
      for (const file of files) {
        const storageRef = ref(storage, `families/${familyBoardId}/events/${selectedEvent.id}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        await addDoc(collection(db, 'families', familyBoardId, 'events', selectedEvent.id, 'photos'), {
          url,
          fileName: file.name,
          uploadedBy: currentUser.uid,
          uploadedByName: currentUser.displayName || 'Anonymous',
          uploadedAt: serverTimestamp()
        });
      }
      setSuccess('Photos uploaded!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      logger.error('Error uploading event photos:', err);
      setError('Failed to upload photos');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeleteEventPhoto = async (photo) => {
    if (!selectedEvent || !familyBoardId) return;
    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'events', selectedEvent.id, 'photos', photo.id));
      setSuccess('Photo removed');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      logger.error('Error deleting event photo:', err);
      setError('Failed to delete photo');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.date) {
      setError('Title and date are required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private events');
      return;
    }

    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        eventType: formData.eventType || 'casual',
        date: formData.date,
        time: formData.time,
        location: formData.location,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        rsvps: {},
        photos: formData.photos || [],
        contactPhone: formData.contactPhone || '',
        contactWebsite: formData.contactWebsite || '',
        contactInstagram: formData.contactInstagram || '',
        contactFacebook: formData.contactFacebook || '',
        contactLinkedIn: formData.contactLinkedIn || '',
        contactTwitter: formData.contactTwitter || '',
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        createdAt: serverTimestamp()
      };

      // Planning fields — available for ALL event types
      eventData.endDate = formData.endDate || '';
      eventData.budgetDetails = formData.budgetDetails || {
        totalBudget: '',
        lineItems: [],
        guestPricing: { adultPrice: '', kidsTiers: [] }
      };
      eventData.schedule = formData.schedule || [];
      eventData.diningStyle = formData.diningStyle || '';
      eventData.mealPlan = formData.mealPlan || [];
      eventData.catererInfo = formData.catererInfo || { name: '', phone: '', website: '', notes: '' };
      eventData.whosBringing = formData.whosBringing || [];
      eventData.hotelLink = formData.hotelLink || '';
      eventData.hotelInfo = formData.hotelInfo || '';
      eventData.venueLocation = formData.venueLocation || null;

      // Reunion-specific extra fields
      if (formData.eventType === 'reunion') {
        eventData.theme = formData.theme;
        eventData.accommodation = formData.accommodation;
        eventData.budget = formData.budget;
      }

      let eventId = editingEvent?.id;
      if (editingEvent) {
        await updateDoc(doc(db, 'families', familyBoardId, 'events', editingEvent.id), {
          ...eventData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Event updated');

        // Send notifications to newly tagged members when editing
        const previousTaggedMembers = editingEvent.taggedMembers || [];
        const newTaggedMembers = (formData.taggedMembers || []).filter(
          memberId => !previousTaggedMembers.includes(memberId)
        );

        if (newTaggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, newTaggedMembers, {
            type: 'event',
            id: editingEvent.id,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/events'
          });
        }
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'events'), eventData);
        eventId = docRef.id;
        setSuccess('Event created');

        // Notify all family members about the new event
        await notifyNewEvent(
          familyBoardId,
          { id: eventId, title: formData.title },
          currentUser.uid,
          currentUser.displayName || profile?.displayName || 'Anonymous'
        );
      }

      setDialogOpen(false);
      setEditingEvent(null);
      resetFormData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving event:', error);
      setError('Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'events', id));
      setSuccess('Event deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting event:', error);
      setError('Failed to delete event');
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      const eventRef = doc(db, 'families', familyBoardId, 'events', eventId);
      const event = events.find(e => e.id === eventId);
      const rsvps = event.rsvps || {};

      rsvps[currentUser.uid] = {
        status,
        name: currentUser.displayName || 'Anonymous',
        timestamp: new Date().toISOString()
      };

      await updateDoc(eventRef, { rsvps });
      setSuccess(`RSVP updated to ${status}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error updating RSVP:', error);
      setError('Failed to update RSVP');
    }
  };

  // Helper function to reset form data
  const resetFormData = () => {
    setFormData({
      title: '',
      description: '',
      eventType: 'casual',
      date: '',
      endDate: '',
      time: '',
      location: null,
      theme: '',
      accommodation: '',
      budget: '',
      budgetDetails: {
        totalBudget: '',
        lineItems: [],
        guestPricing: { adultPrice: '', kidsTiers: [] }
      },
      schedule: [],
      diningStyle: '',
      mealPlan: [],
      catererInfo: { name: '', phone: '', website: '', notes: '' },
      whosBringing: [],
      hotelLink: '',
      hotelInfo: '',
      venueLocation: null,
      photos: [],
      contactPhone: '',
      contactWebsite: '',
      contactInstagram: '',
      contactFacebook: '',
      contactLinkedIn: '',
      contactTwitter: '',
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: []
    });
    setBudgetLineItem({ category: '', description: '', amount: '' });
    setKidsTierItem({ label: '', ageMin: '', ageMax: '', price: '' });
  };

  // Reunion planning helper functions
  const addScheduleItem = () => {
    if (!scheduleItem.day || !scheduleItem.activity) return;
    setFormData({
      ...formData,
      schedule: [...(formData.schedule || []), { ...scheduleItem, id: Date.now() }]
    });
    setScheduleItem({ day: '', time: '', activity: '' });
  };

  const removeScheduleItem = (id) => {
    setFormData({
      ...formData,
      schedule: formData.schedule.filter(item => item.id !== id)
    });
  };

  const addMealItem = () => {
    if (!mealItem.meal || !mealItem.day) return;
    setFormData({
      ...formData,
      mealPlan: [...(formData.mealPlan || []), { ...mealItem, id: Date.now() }]
    });
    setMealItem({ meal: '', day: '', description: '' });
  };

  const removeMealItem = (id) => {
    setFormData({
      ...formData,
      mealPlan: formData.mealPlan.filter(item => item.id !== id)
    });
  };

  const addBringingItem = () => {
    if (!bringingItem.item) return;
    setFormData({
      ...formData,
      whosBringing: [...(formData.whosBringing || []), { ...bringingItem, id: Date.now() }]
    });
    setBringingItem({ item: '', assignedTo: '' });
  };

  const removeBringingItem = (id) => {
    setFormData({
      ...formData,
      whosBringing: formData.whosBringing.filter(item => item.id !== id)
    });
  };

  // Budget helper functions
  const addBudgetLineItem = () => {
    if (!budgetLineItem.category) return;
    const newItem = { ...budgetLineItem, id: Date.now(), amount: parseFloat(budgetLineItem.amount) || 0 };
    setFormData({
      ...formData,
      budgetDetails: {
        ...formData.budgetDetails,
        lineItems: [...(formData.budgetDetails?.lineItems || []), newItem]
      }
    });
    setBudgetLineItem({ category: '', description: '', amount: '' });
  };

  const removeBudgetLineItem = (id) => {
    setFormData({
      ...formData,
      budgetDetails: {
        ...formData.budgetDetails,
        lineItems: (formData.budgetDetails?.lineItems || []).filter(item => item.id !== id)
      }
    });
  };

  const addKidsTier = () => {
    if (!kidsTierItem.label) return;
    const newTier = {
      ...kidsTierItem,
      id: Date.now(),
      ageMin: parseInt(kidsTierItem.ageMin) || 0,
      ageMax: parseInt(kidsTierItem.ageMax) || 0,
      price: parseFloat(kidsTierItem.price) || 0
    };
    setFormData({
      ...formData,
      budgetDetails: {
        ...formData.budgetDetails,
        guestPricing: {
          ...formData.budgetDetails?.guestPricing,
          kidsTiers: [...(formData.budgetDetails?.guestPricing?.kidsTiers || []), newTier]
        }
      }
    });
    setKidsTierItem({ label: '', ageMin: '', ageMax: '', price: '' });
  };

  const removeKidsTier = (id) => {
    setFormData({
      ...formData,
      budgetDetails: {
        ...formData.budgetDetails,
        guestPricing: {
          ...formData.budgetDetails?.guestPricing,
          kidsTiers: (formData.budgetDetails?.guestPricing?.kidsTiers || []).filter(t => t.id !== id)
        }
      }
    });
  };

  // Budget category presets
  const budgetCategories = [
    { value: 'catering', label: 'Catering / Food', icon: <CateringIcon fontSize="small" /> },
    { value: 'venue', label: 'Venue Cost', icon: <VenueIcon fontSize="small" /> },
    { value: 'entertainment', label: 'Entertainment', icon: <EntertainmentIcon fontSize="small" /> },
    { value: 'activities', label: 'Activities', icon: <CelebrationIcon fontSize="small" /> },
    { value: 'gifts', label: 'Gifts / Favors', icon: <GiftIcon fontSize="small" /> },
    { value: 'decorations', label: 'Decorations', icon: <CategoryIcon fontSize="small" /> },
    { value: 'accommodation', label: 'Accommodation', icon: <HotelIcon fontSize="small" /> },
    { value: 'other', label: 'Miscellaneous', icon: <CategoryIcon fontSize="small" /> }
  ];

  // Calculate budget totals
  const getBudgetTotal = (lineItems) => {
    return (lineItems || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  // Enhanced RSVP with guest counts
  const handleRSVPWithGuests = (eventId, status) => {
    if (status === 'going') {
      // Check if this event has guest pricing set up
      const event = events.find(e => e.id === eventId);
      const hasGuestPricing = event?.budgetDetails?.guestPricing?.adultPrice ||
        (event?.budgetDetails?.guestPricing?.kidsTiers || []).length > 0;

      if (hasGuestPricing || event?.eventType === 'reunion') {
        // Open dialog to collect guest counts
        setRsvpEventId(eventId);
        const existingRsvp = event?.rsvps?.[currentUser.uid];
        setRsvpGuests({
          adultsCount: existingRsvp?.adultsCount || 1,
          kidsCount: existingRsvp?.kidsCount || 0,
          kidsByAge: existingRsvp?.kidsByAge || []
        });
        setRsvpDialogOpen(true);
        return;
      }
    }
    // For non-reunion or maybe/notGoing, use simple RSVP
    handleRSVP(eventId, status);
  };

  const handleRSVPSubmitWithGuests = async () => {
    try {
      const eventRef = doc(db, 'families', familyBoardId, 'events', rsvpEventId);
      const event = events.find(e => e.id === rsvpEventId);
      const rsvps = { ...(event?.rsvps || {}) };

      rsvps[currentUser.uid] = {
        status: 'going',
        name: currentUser.displayName || 'Anonymous',
        timestamp: new Date().toISOString(),
        adultsCount: parseInt(rsvpGuests.adultsCount) || 1,
        kidsCount: parseInt(rsvpGuests.kidsCount) || 0,
        kidsByAge: rsvpGuests.kidsByAge || [],
        paid: rsvps[currentUser.uid]?.paid || false
      };

      await updateDoc(eventRef, { rsvps });
      setSuccess('RSVP updated with guest details');
      setTimeout(() => setSuccess(null), 2000);
      setRsvpDialogOpen(false);
    } catch (error) {
      logger.error('Error updating RSVP:', error);
      setError('Failed to update RSVP');
    }
  };

  // Admin: toggle payment status
  const handleTogglePayment = async (eventId, userId) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (!event) return;
      const rsvps = { ...(event.rsvps || {}) };
      if (!rsvps[userId]) return;

      rsvps[userId] = {
        ...rsvps[userId],
        paid: !rsvps[userId].paid
      };

      const eventRef = doc(db, 'families', familyBoardId, 'events', eventId);
      await updateDoc(eventRef, { rsvps });
      setSuccess(`Payment status updated for ${rsvps[userId].name}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error toggling payment:', error);
      setError('Failed to update payment status');
    }
  };

  // Calculate dues for an RSVP entry
  const calculateDues = (rsvp, guestPricing) => {
    if (!guestPricing) return 0;
    const adultTotal = (parseInt(rsvp.adultsCount) || 0) * (parseFloat(guestPricing.adultPrice) || 0);
    let kidsTotal = 0;
    const kidsByAge = rsvp.kidsByAge || [];
    kidsByAge.forEach(kid => {
      const tier = (guestPricing.kidsTiers || []).find(t =>
        kid.age >= t.ageMin && kid.age <= t.ageMax
      );
      kidsTotal += tier ? (parseFloat(tier.price) || 0) : 0;
    });
    // If no kidsByAge data, estimate based on kidsCount and first tier
    if (kidsByAge.length === 0 && (parseInt(rsvp.kidsCount) || 0) > 0 && (guestPricing.kidsTiers || []).length > 0) {
      const avgPrice = (guestPricing.kidsTiers || []).reduce((sum, t) => sum + (parseFloat(t.price) || 0), 0) / guestPricing.kidsTiers.length;
      kidsTotal = (parseInt(rsvp.kidsCount) || 0) * avgPrice;
    }
    return adultTotal + kidsTotal;
  };

  // Export RSVP/Attendee data to CSV (Excel-compatible)
  const exportAttendeesToCSV = (event) => {
    if (!event?.rsvps) return;

    const guestPricing = event.budgetDetails?.guestPricing;
    const hasGuestPricing = !!guestPricing?.adultPrice;

    const headers = ['Name', 'Status', 'Adults', 'Kids', 'Kids Ages'];
    if (hasGuestPricing) {
      headers.push('Dues Owed', 'Paid');
    }
    headers.push('RSVP Date');

    const rows = Object.entries(event.rsvps).map(([userId, rsvp]) => {
      const kidsAges = (rsvp.kidsByAge || []).map(k => k.age).join('; ');
      const statusLabel = rsvp.status === 'going' ? 'Going' :
        rsvp.status === 'maybe' ? 'Maybe' : "Can't Go";
      const row = [
        rsvp.name || 'Unknown',
        statusLabel,
        rsvp.adultsCount || (rsvp.status === 'going' ? 1 : 0),
        rsvp.kidsCount || 0,
        kidsAges || ''
      ];
      if (hasGuestPricing) {
        const dues = rsvp.status === 'going' ? calculateDues(rsvp, guestPricing) : 0;
        row.push(`$${dues.toFixed(2)}`);
        row.push(rsvp.paid ? 'Yes' : 'No');
      }
      row.push(rsvp.timestamp ? new Date(rsvp.timestamp).toLocaleDateString() : '');
      return row;
    });

    // Sort: Going first, then Maybe, then Not Going
    const statusOrder = { going: 0, maybe: 1, notGoing: 2 };
    const entries = Object.entries(event.rsvps);
    const sortedRows = rows
      .map((row, i) => ({ row, status: entries[i][1].status }))
      .sort((a, b) => (statusOrder[a.status] || 9) - (statusOrder[b.status] || 9))
      .map(r => r.row);

    // Add summary rows
    const goingEntries = entries.filter(([_, r]) => r.status === 'going');
    const totalAdults = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.adultsCount) || 1), 0);
    const totalKids = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.kidsCount) || 0), 0);

    sortedRows.push([]); // blank row
    sortedRows.push(['TOTALS (Going)', '', totalAdults, totalKids, '']);
    if (hasGuestPricing) {
      const totalDues = goingEntries.reduce((sum, [_, r]) =>
        sum + calculateDues(r, guestPricing), 0);
      const paidDues = goingEntries.filter(([_, r]) => r.paid).reduce((sum, [_, r]) =>
        sum + calculateDues(r, guestPricing), 0);
      sortedRows[sortedRows.length - 1].push(`$${totalDues.toFixed(2)}`);
      sortedRows[sortedRows.length - 1].push(`${goingEntries.filter(([_, r]) => r.paid).length}/${goingEntries.length}`);
      sortedRows.push(['PAID', '', '', '', '', `$${paidDues.toFixed(2)}`, '']);
      sortedRows.push(['OUTSTANDING', '', '', '', '', `$${(totalDues - paidDues).toFixed(2)}`, '']);
    }

    const csvContent = [
      // Event header info
      `"${event.title}"`,
      `"Date: ${event.date}${event.endDate ? ' - ' + event.endDate : ''}"`,
      `"Location: ${getLocationStr(event.location) || 'TBD'}"`,
      '',
      headers.join(','),
      ...sortedRows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}_attendees.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess('Attendee list exported to CSV');
    setTimeout(() => setSuccess(null), 2000);
  };

  // Print event summary (opens browser print dialog)
  const printEventSummary = (event) => {
    if (!event) return;

    const guestPricing = event.budgetDetails?.guestPricing;
    const hasGuestPricing = !!guestPricing?.adultPrice;
    const goingEntries = Object.entries(event.rsvps || {}).filter(([_, r]) => r.status === 'going');
    const maybeEntries = Object.entries(event.rsvps || {}).filter(([_, r]) => r.status === 'maybe');
    const totalAdults = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.adultsCount) || 1), 0);
    const totalKids = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.kidsCount) || 0), 0);

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to print event summary');
      return;
    }

    let budgetHTML = '';
    if ((event.budgetDetails?.lineItems || []).length > 0) {
      budgetHTML = `
        <h3>Budget Breakdown</h3>
        ${event.budgetDetails.totalBudget ? `<p><strong>Total Budget:</strong> $${parseFloat(event.budgetDetails.totalBudget).toLocaleString()}</p>` : ''}
        <table>
          <thead><tr><th>Category</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
          <tbody>
            ${event.budgetDetails.lineItems.map(item => `
              <tr><td>${item.category}</td><td>${item.description}</td><td style="text-align:right">$${(parseFloat(item.amount) || 0).toFixed(2)}</td></tr>
            `).join('')}
            <tr style="font-weight:bold;border-top:2px solid #333">
              <td colspan="2">Total Expenses</td>
              <td style="text-align:right">$${getBudgetTotal(event.budgetDetails.lineItems).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      `;
    } else if (event.budget) {
      budgetHTML = `
        <h3>Budget</h3>
        <p>${event.budget}</p>
      `;
    }

    let pricingHTML = '';
    if (hasGuestPricing) {
      pricingHTML = `
        <h3>Per-Guest Pricing</h3>
        <table>
          <thead><tr><th>Guest Type</th><th style="text-align:right">Price</th></tr></thead>
          <tbody>
            <tr><td>Adults (18+)</td><td style="text-align:right"><strong>$${parseFloat(guestPricing.adultPrice).toFixed(2)}</strong></td></tr>
            ${(guestPricing.kidsTiers || []).map(tier =>
              `<tr><td>${tier.label} (ages ${tier.ageMin}-${tier.ageMax})</td><td style="text-align:right"><strong>${parseFloat(tier.price) === 0 ? 'Free' : '$' + parseFloat(tier.price).toFixed(2)}</strong></td></tr>`
            ).join('')}
          </tbody>
        </table>
      `;
    }

    let scheduleHTML = '';
    if ((event.schedule || []).length > 0) {
      scheduleHTML = `
        <h3>Daily Schedule</h3>
        <table>
          <thead><tr><th>Day</th><th>Time</th><th>Activity</th></tr></thead>
          <tbody>${event.schedule.map(s => `<tr><td>${s.day}</td><td>${s.time}</td><td>${s.activity}</td></tr>`).join('')}</tbody>
        </table>
      `;
    }

    let mealHTML = '';
    if ((event.mealPlan || []).length > 0 || event.catererInfo?.name) {
      mealHTML = '<h3>Dining & Meals</h3>';
      if (event.diningStyle) {
        const styleLabel = event.diningStyle === 'potluck' ? 'Traditional Potluck' :
          event.diningStyle === 'catered' ? 'Catered Event' : 'Mixed (Catered + Potluck)';
        mealHTML += `<p><strong>Style:</strong> ${styleLabel}</p>`;
      }
      if (event.catererInfo?.name) {
        mealHTML += `<p><strong>Caterer:</strong> ${event.catererInfo.name}`;
        if (event.catererInfo.phone) mealHTML += ` | ${event.catererInfo.phone}`;
        mealHTML += '</p>';
        if (event.catererInfo.notes) mealHTML += `<p>${event.catererInfo.notes}</p>`;
      }
      if ((event.mealPlan || []).length > 0) {
        mealHTML += `
          <table>
            <thead><tr><th>Meal</th><th>Day</th><th>Details</th></tr></thead>
            <tbody>${event.mealPlan.map(m => `<tr><td>${m.meal}</td><td>${m.day}</td><td>${m.description}</td></tr>`).join('')}</tbody>
          </table>
        `;
      }
    }

    let bringingHTML = '';
    if ((event.whosBringing || []).length > 0) {
      bringingHTML = `
        <h3>Who's Bringing What</h3>
        <table>
          <thead><tr><th>Item</th><th>Assigned To</th></tr></thead>
          <tbody>${event.whosBringing.map(w => `<tr><td>${w.item}</td><td>${w.assignedTo || '<em>Unassigned</em>'}</td></tr>`).join('')}</tbody>
        </table>
      `;
    }

    let attendeesHTML = '';
    // Summary only shows counts, not individual names

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${event.title} - Event Summary</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; line-height: 1.5; }
    h1 { color: #2e7d32; border-bottom: 3px solid #2e7d32; padding-bottom: 10px; margin-bottom: 16px; }
    h2 { color: #555; }
    h3 { color: #2e7d32; margin-top: 28px; margin-bottom: 12px; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0 20px; }
    th, td { border: 1px solid #ddd; padding: 10px 12px; text-align: left; font-size: 13px; }
    th { background-color: #f5f5f5; font-weight: 600; }
    tr:nth-child(even) { background-color: #fafafa; }
    .meta { color: #555; margin: 6px 0; font-size: 14px; }
    .meta strong { color: #333; }
    .event-info { margin: 16px 0; padding: 12px 16px; background: #fafafa; border-left: 3px solid #2e7d32; border-radius: 0 4px 4px 0; }
    .event-description { margin: 16px 0; line-height: 1.6; font-size: 14px; color: #444; }
    .summary-box { background: #f0f7f0; border: 1px solid #c8e6c9; border-radius: 8px; padding: 16px; margin: 20px 0; display: flex; gap: 32px; justify-content: center; }
    .summary-item { text-align: center; }
    .summary-item .number { font-size: 28px; font-weight: 700; color: #2e7d32; }
    .summary-item .label { font-size: 12px; color: #666; }
    .hotel-box { background: #f5f9ff; border: 1px solid #bbdefb; border-radius: 6px; padding: 12px 16px; margin: 12px 0; }
    .hotel-box a { color: #1565c0; word-break: break-all; }
    a { color: #1976d2; text-decoration: none; }
    a:hover { text-decoration: underline; }
    @media print {
      body { margin: 0; padding: 10px; }
      .no-print { display: none; }
      .hotel-box { border-color: #ccc; }
    }
  </style>
</head>
<body>
  <h1>${event.title}</h1>
  <div class="event-info">
    <p class="meta"><strong>Date:</strong> ${(() => { try { const d = new Date(event.date + 'T00:00:00'); const fmt = d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); if (event.endDate) { const e = new Date(event.endDate + 'T00:00:00'); return fmt + ' — ' + e.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); } return fmt; } catch(e) { return event.date + (event.endDate ? ' — ' + event.endDate : ''); } })()}${event.time ? ' at ' + event.time : ''}</p>
    ${event.location ? `<p class="meta"><strong>Location:</strong> ${getLocationStr(event.location)}</p>` : ''}
    ${event.eventType ? `<p class="meta"><strong>Event Type:</strong> ${event.eventType === 'reunion' ? 'Family Reunion' : event.eventType === 'casual' ? 'Casual Gathering' : event.eventType === 'holiday' ? 'Holiday Celebration' : event.eventType === 'milestone' ? 'Milestone Event' : event.eventType.charAt(0).toUpperCase() + event.eventType.slice(1)}</p>` : ''}
    ${event.theme ? `<p class="meta"><strong>Theme:</strong> ${event.theme}</p>` : ''}
  </div>
  ${event.description ? `<p class="event-description">${event.description}</p>` : ''}
  ${event.accommodation || event.hotelLink || event.hotelInfo ? `
    <h3>Accommodation & Venue</h3>
    ${event.accommodation ? `<p class="meta">${event.accommodation}</p>` : ''}
    ${event.venueLocation ? `<p class="meta"><strong>Location:</strong> ${getLocationStr(event.venueLocation)}</p>` : ''}
    ${event.hotelLink || event.hotelInfo ? `
      <div class="hotel-box">
        ${event.hotelLink ? `<p style="margin:4px 0"><strong>Book Here:</strong> <a href="${event.hotelLink}">Hotel Reservation Link</a></p>` : ''}
        ${event.hotelInfo ? `<p style="margin:4px 0;white-space:pre-wrap">${event.hotelInfo}</p>` : ''}
      </div>` : ''}
  ` : ''}
  ${event.contactPhone || event.contactWebsite || event.contactInstagram || event.contactFacebook ? `
    <h3>Contact & Social</h3>
    ${event.contactPhone ? `<p class="meta"><strong>Phone:</strong> ${event.contactPhone}</p>` : ''}
    ${event.contactWebsite ? `<p class="meta"><strong>Website:</strong> <a href="${event.contactWebsite}">${event.contactWebsite}</a></p>` : ''}
    ${event.contactInstagram ? `<p class="meta"><strong>Instagram:</strong> ${event.contactInstagram}</p>` : ''}
    ${event.contactFacebook ? `<p class="meta"><strong>Facebook:</strong> ${event.contactFacebook}</p>` : ''}
    ${event.contactLinkedIn ? `<p class="meta"><strong>LinkedIn:</strong> ${event.contactLinkedIn}</p>` : ''}
    ${event.contactTwitter ? `<p class="meta"><strong>Twitter:</strong> ${event.contactTwitter}</p>` : ''}
  ` : ''}

  ${goingEntries.length > 0 || maybeEntries.length > 0 ? `
  <div class="summary-box">
    <div class="summary-item"><div class="number">${totalAdults + totalKids}</div><div class="label">Total Guests</div></div>
    <div class="summary-item"><div class="number">${goingEntries.length}</div><div class="label">Families Going</div></div>
    <div class="summary-item"><div class="number">${totalAdults}</div><div class="label">Adults</div></div>
    <div class="summary-item"><div class="number">${totalKids}</div><div class="label">Kids</div></div>
    <div class="summary-item"><div class="number">${maybeEntries.length}</div><div class="label">Maybe</div></div>
  </div>` : `
  <div class="summary-box" style="justify-content:center">
    <p style="margin:0;color:#666;font-style:italic">No RSVPs yet — share this event to start collecting responses!</p>
  </div>`}

  ${budgetHTML}
  ${pricingHTML}
  ${scheduleHTML}
  ${mealHTML}
  ${bringingHTML}
  ${attendeesHTML}

  <p style="text-align:center;color:#999;margin-top:30px;font-size:11px">
    Generated from FamilyForLife on ${new Date().toLocaleDateString()}
  </p>

  <div class="no-print" style="text-align:center;margin-top:20px">
    <button onclick="window.print()" style="padding:10px 24px;background:#2e7d32;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">
      Print / Save as PDF
    </button>
  </div>
</body>
</html>`);
    printWindow.document.close();
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      eventType: event.eventType || 'casual',
      date: event.date,
      endDate: event.endDate || '',
      time: event.time || '',
      location: typeof event.location === 'string'
        ? (event.location ? { name: event.location, address: event.location } : null)
        : (event.location || null),
      theme: event.theme || '',
      accommodation: event.accommodation || '',
      budget: event.budget || '',
      budgetDetails: event.budgetDetails || {
        totalBudget: '',
        lineItems: [],
        guestPricing: { adultPrice: '', kidsTiers: [] }
      },
      schedule: event.schedule || [],
      diningStyle: event.diningStyle || '',
      mealPlan: event.mealPlan || [],
      catererInfo: event.catererInfo || { name: '', phone: '', website: '', notes: '' },
      whosBringing: event.whosBringing || [],
      hotelLink: event.hotelLink || '',
      hotelInfo: event.hotelInfo || '',
      venueLocation: event.venueLocation || null,
      photos: event.photos || [],
      contactPhone: event.contactPhone || '',
      contactWebsite: event.contactWebsite || '',
      contactInstagram: event.contactInstagram || '',
      contactFacebook: event.contactFacebook || '',
      contactLinkedIn: event.contactLinkedIn || '',
      contactTwitter: event.contactTwitter || '',
      privacy: event.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: event.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingEvent(null);
    resetFormData();
    setDialogOpen(true);
  };

  const getRSVPCounts = (rsvps) => {
    const counts = { going: 0, notGoing: 0, maybe: 0 };
    if (!rsvps) return counts;

    Object.values(rsvps).forEach(rsvp => {
      if (rsvp.status === 'going') counts.going++;
      else if (rsvp.status === 'notGoing') counts.notGoing++;
      else if (rsvp.status === 'maybe') counts.maybe++;
    });

    return counts;
  };

  const getUserRSVP = (rsvps) => {
    if (!rsvps || !currentUser) return null;
    return rsvps[currentUser.uid]?.status || null;
  };

  const openDetailsDialog = (event, tab = 0) => {
    setSelectedEvent(event);
    setDetailsTab(tab);
    setDetailsDialogOpen(true);
    setNewComment('');
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedEvent(null);
    setDetailsTab(0);
    setComments([]);
    setEventPhotos([]);
    setNewComment('');
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    try {
      await addDoc(collection(db, 'families', familyBoardId, 'events', selectedEvent.id, 'comments'), {
        content: newComment,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      });

      setNewComment('');
      setSuccess('Comment added');
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error adding comment:', error);
      setError('Failed to add comment');
    }
  };

  // Helper function to check if user can view an event based on privacy settings
  const canViewEvent = (event) => {
    // Event author can always see their own events
    if (event.authorId === currentUser?.uid) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!event.privacy || event.privacy.visibility === 'public') {
      return true;
    }

    // For private events, check if user is in the allowed list
    if (event.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (event.privacy.memberIds && event.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (event.privacy.groupTypes && event.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = event.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private event
      return false;
    }

    return true;
  };

  // Filter events based on privacy settings and date
  const visibleEvents = events.filter(event => canViewEvent(event));

  const upcomingEvents = visibleEvents.filter(event => {
    const eventDate = parseISO(event.date);
    return isFuture(eventDate) || format(eventDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  });

  const pastEvents = visibleEvents.filter(event => {
    const eventDate = parseISO(event.date);
    return isPast(eventDate) && format(eventDate, 'yyyy-MM-dd') !== format(new Date(), 'yyyy-MM-dd');
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule and track upcoming family gatherings
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          New Event
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Upcoming Events
          </Typography>
          <Grid container spacing={3}>
            {upcomingEvents.map((event) => {
              const rsvpCounts = getRSVPCounts(event.rsvps);
              const userRSVP = getUserRSVP(event.rsvps);

              return (
                <Grid item xs={12} md={6} key={event.id}>
                  <Card sx={{
                    '&:hover': { boxShadow: 6 },
                    transition: 'all 0.3s',
                    border: '2px solid',
                    borderColor: 'primary.light',
                    cursor: 'pointer'
                  }}
                  onClick={() => openDetailsDialog(event)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 2 }}>
                        <Avatar sx={{
                          bgcolor: eventTypes.find(t => t.value === (event.eventType || 'casual'))?.color || 'primary.main',
                          width: { xs: 40, sm: 56 },
                          height: { xs: 40, sm: 56 },
                          flexShrink: 0,
                          '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 32 } }
                        }}>
                          {eventTypes.find(t => t.value === (event.eventType || 'casual'))?.icon || <CalendarIcon sx={{ fontSize: 32 }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, minWidth: 0, wordBreak: 'break-word' }}>
                              {event.title}
                            </Typography>
                            {event.eventType && event.eventType !== 'casual' && (
                              <Chip
                                label={eventTypes.find(t => t.value === event.eventType)?.label}
                                size="small"
                                sx={{
                                  bgcolor: eventTypes.find(t => t.value === event.eventType)?.color,
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                          {event.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {event.description}
                            </Typography>
                          )}
                          {event.theme && (
                            <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CelebrationIcon fontSize="small" /> Theme: {event.theme}
                            </Typography>
                          )}
                        </Box>
                        {event.authorId === currentUser.uid && (
                          <Box>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(event); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon fontSize="small" sx={{ color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}
                            {event.endDate && event.endDate !== event.date && (
                              <> - {format(parseISO(event.endDate), 'MMMM d, yyyy')}</>
                            )}
                          </Typography>
                        </Box>
                        {event.time && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {event.time}
                            </Typography>
                          </Box>
                        )}
                        {event.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <PlaceIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {getLocationStr(event.location)}
                            </Typography>
                            {buildMapsUrl(normalizeLocation(event.location)) && (
                              <Button size="small" variant="text" sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                onClick={(e) => { e.stopPropagation(); window.open(buildMapsUrl(normalizeLocation(event.location)), '_blank'); }}>
                                Map
                              </Button>
                            )}
                            {buildDirectionsUrl(normalizeLocation(event.location)) && (
                              <Button size="small" variant="text" startIcon={<DirectionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                                sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                onClick={(e) => { e.stopPropagation(); window.open(buildDirectionsUrl(normalizeLocation(event.location)), '_blank'); }}>
                                Directions
                              </Button>
                            )}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Created by {event.authorName}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* RSVP Section */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Will you attend?
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Button
                            size="small"
                            variant={userRSVP === 'going' ? 'contained' : 'outlined'}
                            color="success"
                            startIcon={<GoingIcon />}
                            onClick={(e) => { e.stopPropagation(); handleRSVPWithGuests(event.id, 'going'); }}
                          >
                            Going ({rsvpCounts.going})
                          </Button>
                          <Button
                            size="small"
                            variant={userRSVP === 'maybe' ? 'contained' : 'outlined'}
                            color="warning"
                            startIcon={<MaybeIcon />}
                            onClick={(e) => { e.stopPropagation(); handleRSVPWithGuests(event.id, 'maybe'); }}
                          >
                            Maybe ({rsvpCounts.maybe})
                          </Button>
                          <Button
                            size="small"
                            variant={userRSVP === 'notGoing' ? 'contained' : 'outlined'}
                            color="error"
                            startIcon={<NotGoingIcon />}
                            onClick={(e) => { e.stopPropagation(); handleRSVPWithGuests(event.id, 'notGoing'); }}
                          >
                            Can't Go ({rsvpCounts.notGoing})
                          </Button>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Event Actions */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<CommentIcon />}
                            onClick={(e) => { e.stopPropagation(); openDetailsDialog(event, 0); }}
                          >
                            Comments
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<PeopleIcon />}
                            onClick={(e) => { e.stopPropagation(); openDetailsDialog(event, 1); }}
                          >
                            Attendees ({rsvpCounts.going})
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<PhotoIcon />}
                            onClick={(e) => { e.stopPropagation(); openDetailsDialog(event, 2); }}
                          >
                            Photos
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
            Past Events
          </Typography>
          <Grid container spacing={2}>
            {pastEvents.map((event) => {
              const rsvpCounts = getRSVPCounts(event.rsvps);

              return (
                <Grid item xs={12} key={event.id}>
                  <Card
                    sx={{
                      opacity: 0.7,
                      '&:hover': { opacity: 1 },
                      transition: 'opacity 0.3s',
                      cursor: 'pointer'
                    }}
                    onClick={() => openDetailsDialog(event)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Avatar sx={{ bgcolor: 'text.secondary', width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 }, flexShrink: 0 }}>
                          <EventIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(event.date), 'MMMM d, yyyy')} • {rsvpCounts.going} attended
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<CommentIcon />}
                              onClick={(e) => { e.stopPropagation(); openDetailsDialog(event, 0); }}
                            >
                              Comments
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<PhotoIcon />}
                              onClick={(e) => { e.stopPropagation(); openDetailsDialog(event, 2); }}
                            >
                              Photos
                            </Button>
                          </Box>
                        </Box>
                        {event.authorId === currentUser.uid && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Empty State */}
      {events.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No events scheduled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start planning family gatherings and special occasions!
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Create First Event
          </Button>
        </Paper>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {editingEvent ? 'Edit Event' : 'Create New Event'}
          {formData.eventType === 'reunion' && (
            <Typography variant="caption" display="block" color="text.secondary">
              Family Reunion Planning Template
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Event Type Selector */}
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.eventType}
                label="Event Type"
                onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      <Typography>{type.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Basic Event Fields */}
            <TextField
              fullWidth
              label="Event Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder={formData.eventType === 'reunion' ? 'e.g., Smith Family Reunion 2026' : 'e.g., Family Dinner'}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder={formData.eventType === 'reunion' ? 'Describe the reunion theme, purpose, or special activities...' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, description: formData.description + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={formData.eventType === 'reunion' ? 6 : 12}>
                <TextField
                  fullWidth
                  label={formData.eventType === 'reunion' ? 'Start Date' : 'Date'}
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              {formData.eventType === 'reunion' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </Grid>

            <TextField
              fullWidth
              label="Time"
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <LocationPicker
              value={formData.location}
              onChange={(loc) => setFormData({ ...formData, location: loc })}
              label="Location"
              placeholder="e.g., Grandma's house, City Park"
            />

            {/* Photo Upload */}
            <Box>
              <input
                type="file"
                ref={formPhotoInputRef}
                onChange={handleFormPhotoUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              <Button
                startIcon={photoUploading ? <CircularProgress size={16} /> : <PhotoIcon />}
                onClick={() => formPhotoInputRef.current?.click()}
                disabled={photoUploading || formData.photos.length >= 5}
                variant="outlined"
                size="small"
              >
                {photoUploading ? 'Uploading...' : `Add Photos (${formData.photos.length}/5)`}
              </Button>
              {formData.photos.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {formData.photos.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img
                        src={sanitizeImageUrl(url)}
                        alt={`Event ${index + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeFormPhoto(index)}
                        sx={{
                          position: 'absolute', top: -8, right: -8,
                          bgcolor: 'background.paper', boxShadow: 1,
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }}>
              <Chip label="Social & Contact Info" size="small" />
            </Divider>

            {/* Social Contact Info — 2 columns on sm+, full width on mobile */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder="(555) 123-4567"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Website"
                  value={formData.contactWebsite}
                  onChange={(e) => setFormData({ ...formData, contactWebsite: e.target.value })}
                  placeholder="https://example.com"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram"
                  value={formData.contactInstagram}
                  onChange={(e) => setFormData({ ...formData, contactInstagram: e.target.value })}
                  placeholder="@username"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Facebook"
                  value={formData.contactFacebook}
                  onChange={(e) => setFormData({ ...formData, contactFacebook: e.target.value })}
                  placeholder="facebook.com/page"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="LinkedIn"
                  value={formData.contactLinkedIn}
                  onChange={(e) => setFormData({ ...formData, contactLinkedIn: e.target.value })}
                  placeholder="linkedin.com/in/profile"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Twitter / X"
                  value={formData.contactTwitter}
                  onChange={(e) => setFormData({ ...formData, contactTwitter: e.target.value })}
                  placeholder="@handle"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            {/* Privacy Control */}
            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this event?"
            />

            {/* Tag Members */}
            <Box sx={{ mt: 2 }}>
              <TagMembers
                familyCode={familyBoardId}
                value={formData.taggedMembers}
                onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
                label="Tag Family Members"
                helperText="Tagged members will receive a notification about this event"
              />
            </Box>

            {/* Reunion-Only Fields */}
            {formData.eventType === 'reunion' && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Reunion Details" color="primary" />
                </Divider>

                <TextField
                  fullWidth
                  label="Reunion Theme"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder="e.g., 'Roots & Branches', 'Caribbean Adventure'"
                />

                <TextField
                  fullWidth
                  label="Accommodation Info"
                  value={formData.accommodation}
                  onChange={(e) => setFormData({ ...formData, accommodation: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Hotel name, address, room block info, check-in/check-out times..."
                />
              </>
            )}

            {/* Event Planning Details — Available for ALL event types */}
            <Divider sx={{ my: 2 }}>
              <Chip label="Event Planning" size="small" />
            </Divider>

            {/* Multi-day: End Date */}
            {formData.eventType !== 'reunion' && (
              <TextField
                fullWidth
                label="End Date (for multi-day events)"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}

            {/* Hotel / Venue Link */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <HotelIcon sx={{ mr: 1 }} />
                <Typography>Venue & Hotel Info</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <LocationPicker
                    value={formData.venueLocation}
                    onChange={(loc) => setFormData({ ...formData, venueLocation: loc })}
                    label="Venue / Hotel Location"
                    placeholder="Search hotel name, venue address, or use GPS"
                  />
                  <TextField
                    fullWidth
                    label="Hotel / Venue Registration Link"
                    value={formData.hotelLink}
                    onChange={(e) => setFormData({ ...formData, hotelLink: e.target.value })}
                    placeholder="https://hotel.com/booking-link"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" /></InputAdornment>
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    value={formData.hotelInfo}
                    onChange={(e) => setFormData({ ...formData, hotelInfo: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Group rate code, deadline to book, parking info, nearby alternatives..."
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

                {/* Enhanced Budget Section */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <MoneyIcon sx={{ mr: 1 }} />
                    <Typography>Budget & Expenses ({(formData.budgetDetails?.lineItems || []).length} items)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Total Budget */}
                      <TextField
                        fullWidth
                        label="Total Event Budget"
                        value={formData.budgetDetails?.totalBudget || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          budgetDetails: { ...formData.budgetDetails, totalBudget: e.target.value }
                        })}
                        placeholder="5000"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />

                      {/* Budget Line Items */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                        Budget Breakdown
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
                          <InputLabel>Category</InputLabel>
                          <Select
                            value={budgetLineItem.category}
                            label="Category"
                            onChange={(e) => setBudgetLineItem({ ...budgetLineItem, category: e.target.value })}
                          >
                            {budgetCategories.map(cat => (
                              <MenuItem key={cat.value} value={cat.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {cat.icon} {cat.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Description"
                          value={budgetLineItem.description}
                          onChange={(e) => setBudgetLineItem({ ...budgetLineItem, description: e.target.value })}
                          placeholder="BBQ catering for 50"
                          sx={{ flex: 2, minWidth: 150 }}
                        />
                        <TextField
                          size="small"
                          label="Amount"
                          value={budgetLineItem.amount}
                          onChange={(e) => setBudgetLineItem({ ...budgetLineItem, amount: e.target.value })}
                          placeholder="500"
                          type="number"
                          sx={{ flex: 0.7, minWidth: 90 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                        <Button onClick={addBudgetLineItem} variant="contained" size="small" sx={{ minWidth: 40 }}>
                          <AddIcon />
                        </Button>
                      </Box>

                      {(formData.budgetDetails?.lineItems || []).length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Category</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell width={40}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(formData.budgetDetails?.lineItems || []).map(item => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <Chip
                                    label={budgetCategories.find(c => c.value === item.category)?.label || item.category}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell align="right">${(parseFloat(item.amount) || 0).toFixed(2)}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeBudgetLineItem(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Expenses</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                ${getBudgetTotal(formData.budgetDetails?.lineItems).toFixed(2)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </TableBody>
                        </Table>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* Per-Guest Pricing */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Per-Guest Pricing (Reunion Dues)
                      </Typography>
                      <TextField
                        size="small"
                        label="Adult Price (per person)"
                        value={formData.budgetDetails?.guestPricing?.adultPrice || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          budgetDetails: {
                            ...formData.budgetDetails,
                            guestPricing: {
                              ...formData.budgetDetails?.guestPricing,
                              adultPrice: e.target.value
                            }
                          }
                        })}
                        placeholder="100"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />

                      <Typography variant="caption" color="text.secondary">
                        Kids pricing tiers (set age ranges and pricing for children)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          label="Label"
                          value={kidsTierItem.label}
                          onChange={(e) => setKidsTierItem({ ...kidsTierItem, label: e.target.value })}
                          placeholder="Ages 0-5"
                          sx={{ flex: 1.5, minWidth: 100 }}
                        />
                        <TextField
                          size="small"
                          label="Age Min"
                          value={kidsTierItem.ageMin}
                          onChange={(e) => setKidsTierItem({ ...kidsTierItem, ageMin: e.target.value })}
                          type="number"
                          sx={{ flex: 0.5, minWidth: 70 }}
                        />
                        <TextField
                          size="small"
                          label="Age Max"
                          value={kidsTierItem.ageMax}
                          onChange={(e) => setKidsTierItem({ ...kidsTierItem, ageMax: e.target.value })}
                          type="number"
                          sx={{ flex: 0.5, minWidth: 70 }}
                        />
                        <TextField
                          size="small"
                          label="Price"
                          value={kidsTierItem.price}
                          onChange={(e) => setKidsTierItem({ ...kidsTierItem, price: e.target.value })}
                          type="number"
                          sx={{ flex: 0.7, minWidth: 80 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                        <Button onClick={addKidsTier} variant="contained" size="small" sx={{ minWidth: 40 }}>
                          <AddIcon />
                        </Button>
                      </Box>

                      {(formData.budgetDetails?.guestPricing?.kidsTiers || []).length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Tier</TableCell>
                              <TableCell>Ages</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell width={40}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(formData.budgetDetails?.guestPricing?.kidsTiers || []).map(tier => (
                              <TableRow key={tier.id}>
                                <TableCell>{tier.label}</TableCell>
                                <TableCell>{tier.ageMin} - {tier.ageMax}</TableCell>
                                <TableCell align="right">
                                  {parseFloat(tier.price) === 0 ? 'Free' : `$${(parseFloat(tier.price) || 0).toFixed(2)}`}
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeKidsTier(tier.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Daily Schedule */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <ScheduleIcon sx={{ mr: 1 }} />
                    <Typography>Daily Schedule ({formData.schedule?.length || 0} items)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Day"
                          value={scheduleItem.day}
                          onChange={(e) => setScheduleItem({ ...scheduleItem, day: e.target.value })}
                          placeholder="Saturday"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Time"
                          value={scheduleItem.time}
                          onChange={(e) => setScheduleItem({ ...scheduleItem, time: e.target.value })}
                          placeholder="2:00 PM"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Activity"
                          value={scheduleItem.activity}
                          onChange={(e) => setScheduleItem({ ...scheduleItem, activity: e.target.value })}
                          placeholder="Group photo"
                          sx={{ flex: 2 }}
                        />
                        <Button onClick={addScheduleItem} variant="contained" size="small">
                          <AddIcon />
                        </Button>
                      </Box>

                      {formData.schedule && formData.schedule.length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Day</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell>Activity</TableCell>
                              <TableCell width={50}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.schedule.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.day}</TableCell>
                                <TableCell>{item.time}</TableCell>
                                <TableCell>{item.activity}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeScheduleItem(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Enhanced Meal Planning */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <RestaurantIcon sx={{ mr: 1 }} />
                    <Typography>Meal Planning ({formData.mealPlan?.length || 0} meals)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Dining Style Selector */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Dining Style</InputLabel>
                        <Select
                          value={formData.diningStyle || ''}
                          label="Dining Style"
                          onChange={(e) => setFormData({ ...formData, diningStyle: e.target.value })}
                        >
                          <MenuItem value="">
                            <em>Not specified</em>
                          </MenuItem>
                          <MenuItem value="potluck">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HomeIcon fontSize="small" /> Traditional Potluck Gathering
                            </Box>
                          </MenuItem>
                          <MenuItem value="catered">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CateringIcon fontSize="small" /> Catered Event
                            </Box>
                          </MenuItem>
                          <MenuItem value="mixed">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <RestaurantIcon fontSize="small" /> Mixed (Catered + Potluck)
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {/* Caterer Info (available for all dining styles) */}
                      {(formData.diningStyle === 'catered' || formData.diningStyle === 'mixed' || formData.diningStyle === 'potluck') && (
                        <Accordion variant="outlined" sx={{ '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <CateringIcon sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="body2">
                              {formData.diningStyle === 'potluck' ? 'Add Caterer (Optional)' : 'Caterer Information'}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Caterer / Restaurant Name"
                                value={formData.catererInfo?.name || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  catererInfo: { ...formData.catererInfo, name: e.target.value }
                                })}
                                placeholder="Smith's BBQ Catering"
                              />
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="Phone"
                                    value={formData.catererInfo?.phone || ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      catererInfo: { ...formData.catererInfo, phone: e.target.value }
                                    })}
                                    placeholder="(555) 123-4567"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label="Website"
                                    value={formData.catererInfo?.website || ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      catererInfo: { ...formData.catererInfo, website: e.target.value }
                                    })}
                                    placeholder="https://caterer.com"
                                  />
                                </Grid>
                              </Grid>
                              <TextField
                                size="small"
                                fullWidth
                                label="Menu / Notes"
                                value={formData.catererInfo?.notes || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  catererInfo: { ...formData.catererInfo, notes: e.target.value }
                                })}
                                multiline
                                rows={2}
                                placeholder="Menu selections, dietary accommodations, delivery time..."
                              />
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Meal Schedule */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                        Meal Schedule
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Meal"
                          value={mealItem.meal}
                          onChange={(e) => setMealItem({ ...mealItem, meal: e.target.value })}
                          placeholder="Breakfast"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Day"
                          value={mealItem.day}
                          onChange={(e) => setMealItem({ ...mealItem, day: e.target.value })}
                          placeholder="Saturday"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Details"
                          value={mealItem.description}
                          onChange={(e) => setMealItem({ ...mealItem, description: e.target.value })}
                          placeholder={formData.diningStyle === 'catered' ? 'Catered BBQ buffet' : 'Pancakes & eggs'}
                          sx={{ flex: 2 }}
                        />
                        <Button onClick={addMealItem} variant="contained" size="small">
                          <AddIcon />
                        </Button>
                      </Box>

                      {formData.mealPlan && formData.mealPlan.length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Meal</TableCell>
                              <TableCell>Day</TableCell>
                              <TableCell>Details</TableCell>
                              <TableCell width={50}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.mealPlan.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.meal}</TableCell>
                                <TableCell>{item.day}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeMealItem(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Who's Bringing What (universal - available for all dining styles) */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <HomeIcon sx={{ mr: 1 }} />
                    <Typography>Who's Bringing What ({formData.whosBringing?.length || 0} items)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Assign items for family members to bring — works for potlucks, catered events (e.g., someone baking a special cake), or any gathering.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Item"
                          value={bringingItem.item}
                          onChange={(e) => setBringingItem({ ...bringingItem, item: e.target.value })}
                          placeholder="Potato salad"
                          sx={{ flex: 2 }}
                        />
                        <TextField
                          size="small"
                          label="Assigned To"
                          value={bringingItem.assignedTo}
                          onChange={(e) => setBringingItem({ ...bringingItem, assignedTo: e.target.value })}
                          placeholder="Aunt Sarah"
                          sx={{ flex: 1 }}
                        />
                        <Button onClick={addBringingItem} variant="contained" size="small">
                          <AddIcon />
                        </Button>
                      </Box>

                      {formData.whosBringing && formData.whosBringing.length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item</TableCell>
                              <TableCell>Assigned To</TableCell>
                              <TableCell width={50}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.whosBringing.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.item}</TableCell>
                                <TableCell>{item.assignedTo || <em style={{ color: '#999' }}>Unassigned</em>}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => removeBringingItem(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingEvent ? 'Update' : 'Create'} Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Dialog (Comments, Photos, Attendees) */}
      <Dialog
        open={detailsDialogOpen}
        onClose={closeDetailsDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedEvent.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(selectedEvent.date), 'EEEE, MMMM d, yyyy')}
                  {selectedEvent.time && ` at ${selectedEvent.time}`}
                </Typography>
              </Box>
              <IconButton onClick={closeDetailsDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {/* Event Description */}
              <Box sx={{ px: 3, pt: 2 }}>
                {selectedEvent.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.description}
                    </Typography>
                  </Box>
                )}

                {/* Location */}
                {selectedEvent.location && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlaceIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{getLocationStr(selectedEvent.location)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, ml: 3.5 }}>
                      {buildMapsUrl(normalizeLocation(selectedEvent.location)) && (
                        <Button size="small" variant="outlined" startIcon={<PlaceIcon />}
                          onClick={() => window.open(buildMapsUrl(normalizeLocation(selectedEvent.location)), '_blank')}>
                          View on Maps
                        </Button>
                      )}
                      {buildDirectionsUrl(normalizeLocation(selectedEvent.location)) && (
                        <Button size="small" variant="outlined" startIcon={<DirectionsIcon />}
                          onClick={() => window.open(buildDirectionsUrl(normalizeLocation(selectedEvent.location)), '_blank')}>
                          Directions
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Social Contact Info Display */}
                {(selectedEvent.contactPhone || selectedEvent.contactWebsite || selectedEvent.contactInstagram || selectedEvent.contactFacebook || selectedEvent.contactLinkedIn || selectedEvent.contactTwitter) && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {selectedEvent.contactPhone && (
                      <Chip icon={<PhoneIcon />} label={selectedEvent.contactPhone} size="small" variant="outlined" />
                    )}
                    {selectedEvent.contactWebsite && (
                      <Chip icon={<WebsiteIcon />} label={selectedEvent.contactWebsite} size="small" variant="outlined" clickable component="a" href={selectedEvent.contactWebsite.startsWith('http') ? selectedEvent.contactWebsite : `https://${selectedEvent.contactWebsite}`} target="_blank" rel="noopener noreferrer" />
                    )}
                    {selectedEvent.contactInstagram && (
                      <Chip icon={<InstagramIcon />} label={selectedEvent.contactInstagram} size="small" variant="outlined" />
                    )}
                    {selectedEvent.contactFacebook && (
                      <Chip icon={<FacebookIcon />} label={selectedEvent.contactFacebook} size="small" variant="outlined" />
                    )}
                    {selectedEvent.contactLinkedIn && (
                      <Chip icon={<LinkedInIcon />} label={selectedEvent.contactLinkedIn} size="small" variant="outlined" clickable component="a" href={selectedEvent.contactLinkedIn.startsWith('http') ? selectedEvent.contactLinkedIn : `https://${selectedEvent.contactLinkedIn}`} target="_blank" rel="noopener noreferrer" />
                    )}
                    {selectedEvent.contactTwitter && (
                      <Chip label={`X: ${selectedEvent.contactTwitter}`} size="small" variant="outlined" />
                    )}
                  </Box>
                )}

                {/* Event Cover Photos */}
                {selectedEvent.photos && selectedEvent.photos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <ImageList cols={Math.min(selectedEvent.photos.length, 3)} gap={8} sx={{ mt: 0 }}>
                      {selectedEvent.photos.map((url, idx) => (
                        <ImageListItem key={idx}>
                          <img src={sanitizeImageUrl(url)} alt={`Event ${idx + 1}`} style={{ borderRadius: 8, maxHeight: 200, objectFit: 'cover' }} />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                )}

                {/* Reunion-Only Details */}
                {selectedEvent.eventType === 'reunion' && (
                  <>
                    {selectedEvent.theme && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CelebrationIcon fontSize="small" /> Theme
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{selectedEvent.theme}</Typography>
                      </Box>
                    )}

                    {selectedEvent.accommodation && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HotelIcon fontSize="small" /> Accommodation
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedEvent.accommodation}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* Event Planning Details — All Event Types */}
                {(selectedEvent.venueLocation || selectedEvent.hotelLink || selectedEvent.hotelInfo || selectedEvent.budgetDetails?.totalBudget ||
                  (selectedEvent.budgetDetails?.lineItems || []).length > 0 ||
                  selectedEvent.budgetDetails?.guestPricing?.adultPrice ||
                  (selectedEvent.schedule || []).length > 0 ||
                  (selectedEvent.mealPlan || []).length > 0 ||
                  selectedEvent.catererInfo?.name ||
                  (selectedEvent.whosBringing || []).length > 0 ||
                  selectedEvent.budget) && (
                  <>
                    <Divider sx={{ my: 2 }}>
                      <Chip label="Event Planning" color="primary" size="small" />
                    </Divider>

                    {/* Hotel / Venue Link */}
                    {(selectedEvent.venueLocation || selectedEvent.hotelLink || selectedEvent.hotelInfo) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HotelIcon fontSize="small" /> Venue & Hotel
                        </Typography>
                        {selectedEvent.venueLocation && (
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PlaceIcon fontSize="small" color="primary" />
                              <Typography variant="body2">{getLocationStr(selectedEvent.venueLocation)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, ml: 3.5 }}>
                              {buildMapsUrl(normalizeLocation(selectedEvent.venueLocation)) && (
                                <Button size="small" variant="outlined" startIcon={<PlaceIcon />}
                                  onClick={() => window.open(buildMapsUrl(normalizeLocation(selectedEvent.venueLocation)), '_blank')}>
                                  View on Maps
                                </Button>
                              )}
                              {buildDirectionsUrl(normalizeLocation(selectedEvent.venueLocation)) && (
                                <Button size="small" variant="outlined" startIcon={<DirectionsIcon />}
                                  onClick={() => window.open(buildDirectionsUrl(normalizeLocation(selectedEvent.venueLocation)), '_blank')}>
                                  Directions
                                </Button>
                              )}
                            </Box>
                          </Box>
                        )}
                        {selectedEvent.hotelLink && (
                          <Chip
                            icon={<WebsiteIcon />}
                            label="Hotel Registration / Booking Link"
                            size="small"
                            variant="outlined"
                            clickable
                            component="a"
                            href={selectedEvent.hotelLink.startsWith('http') ? selectedEvent.hotelLink : `https://${selectedEvent.hotelLink}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mb: 1 }}
                          />
                        )}
                        {selectedEvent.hotelInfo && (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                            {selectedEvent.hotelInfo}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Budget Display */}
                    {(selectedEvent.budgetDetails?.totalBudget || (selectedEvent.budgetDetails?.lineItems || []).length > 0 || selectedEvent.budget) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon fontSize="small" /> Budget & Expenses
                        </Typography>

                        {/* Legacy budget text */}
                        {selectedEvent.budget && !selectedEvent.budgetDetails?.totalBudget && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{selectedEvent.budget}</Typography>
                        )}

                        {selectedEvent.budgetDetails?.totalBudget && (
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            Total Budget: ${parseFloat(selectedEvent.budgetDetails.totalBudget).toLocaleString()}
                          </Typography>
                        )}

                        {(selectedEvent.budgetDetails?.lineItems || []).length > 0 && (
                          <Table size="small" sx={{ mb: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedEvent.budgetDetails.lineItems.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <Chip label={item.category} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>{item.description}</TableCell>
                                  <TableCell align="right">${(parseFloat(item.amount) || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Expenses</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  ${getBudgetTotal(selectedEvent.budgetDetails.lineItems).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    )}

                    {/* Per-Guest Pricing Display */}
                    {(selectedEvent.budgetDetails?.guestPricing?.adultPrice || (selectedEvent.budgetDetails?.guestPricing?.kidsTiers || []).length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReceiptIcon fontSize="small" /> Per-Guest Pricing (Dues)
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          {selectedEvent.budgetDetails.guestPricing.adultPrice && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Adults: <strong>${parseFloat(selectedEvent.budgetDetails.guestPricing.adultPrice).toFixed(2)}</strong> per person
                            </Typography>
                          )}
                          {(selectedEvent.budgetDetails.guestPricing.kidsTiers || []).map((tier, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                              {tier.label} (ages {tier.ageMin}-{tier.ageMax}): <strong>{parseFloat(tier.price) === 0 ? 'Free' : `$${parseFloat(tier.price).toFixed(2)}`}</strong>
                            </Typography>
                          ))}
                        </Paper>
                      </Box>
                    )}

                    {/* Schedule */}
                    {selectedEvent.schedule && selectedEvent.schedule.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon fontSize="small" /> Daily Schedule
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Day</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedEvent.schedule.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{item.day}</TableCell>
                                <TableCell>{item.time}</TableCell>
                                <TableCell>{item.activity}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}

                    {/* Dining & Meal Plan */}
                    {(selectedEvent.diningStyle || (selectedEvent.mealPlan || []).length > 0 || selectedEvent.catererInfo?.name) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RestaurantIcon fontSize="small" /> Dining & Meals
                        </Typography>

                        {selectedEvent.diningStyle && (
                          <Chip
                            label={selectedEvent.diningStyle === 'potluck' ? 'Traditional Potluck' :
                              selectedEvent.diningStyle === 'catered' ? 'Catered Event' :
                              selectedEvent.diningStyle === 'mixed' ? 'Mixed (Catered + Potluck)' : selectedEvent.diningStyle}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}

                        {/* Caterer Info */}
                        {selectedEvent.catererInfo?.name && (
                          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              <CateringIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              {selectedEvent.catererInfo.name}
                            </Typography>
                            {selectedEvent.catererInfo.phone && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Phone: {selectedEvent.catererInfo.phone}
                              </Typography>
                            )}
                            {selectedEvent.catererInfo.website && (
                              <Chip
                                icon={<WebsiteIcon />}
                                label={selectedEvent.catererInfo.website}
                                size="small"
                                variant="outlined"
                                clickable
                                component="a"
                                href={selectedEvent.catererInfo.website.startsWith('http') ? selectedEvent.catererInfo.website : `https://${selectedEvent.catererInfo.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                            {selectedEvent.catererInfo.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                {selectedEvent.catererInfo.notes}
                              </Typography>
                            )}
                          </Paper>
                        )}

                        {/* Meal Schedule Table */}
                        {(selectedEvent.mealPlan || []).length > 0 && (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Meal</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Day</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedEvent.mealPlan.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{item.meal}</TableCell>
                                  <TableCell>{item.day}</TableCell>
                                  <TableCell>{item.description}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    )}

                    {/* Who's Bringing What */}
                    {selectedEvent.whosBringing && selectedEvent.whosBringing.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HomeIcon fontSize="small" /> Who's Bringing What
                        </Typography>
                        <List dense>
                          {selectedEvent.whosBringing.map((item, idx) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={item.item}
                                secondary={item.assignedTo || 'Unassigned'}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Tabs for Comments / Attendees / Photos */}
              <Tabs
                value={detailsTab}
                onChange={(_, newValue) => setDetailsTab(newValue)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab icon={<CommentIcon />} label={`Comments (${comments.length})`} iconPosition="start" />
                <Tab icon={<PeopleIcon />} label={`Attendees (${getRSVPCounts(selectedEvent.rsvps).going})`} iconPosition="start" />
                <Tab icon={<GalleryIcon />} label={`Photos (${eventPhotos.length})`} iconPosition="start" />
              </Tabs>

              <Box sx={{ px: 3, py: 2 }}>
                {/* Comments Tab */}
                {detailsTab === 0 && (
                  <Box>
                    <Box sx={{ mb: 2, maxHeight: '300px', overflowY: 'auto' }}>
                      {loadingComments ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : comments.length > 0 ? (
                        comments.map((comment) => (
                          <Paper key={comment.id} sx={{ p: 2, mb: 1.5, bgcolor: 'background.default' }}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {comment.authorName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {comment.content}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {comment.createdAt && formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        ))
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <CommentIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No comments yet. Be the first to comment!
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <EmojiPicker
                                onEmojiSelect={(emoji) => setNewComment(prev => prev + emoji)}
                                size="small"
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                      <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        sx={{ minWidth: 100 }}
                      >
                        Post
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Enhanced Attendees Tab with Guest Counts & Payment Tracking */}
                {detailsTab === 1 && (
                  <Box>
                    {/* Export & Print Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportAttendeesToCSV(selectedEvent)}
                        disabled={!selectedEvent?.rsvps || Object.keys(selectedEvent.rsvps).length === 0}
                      >
                        Export CSV
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => printEventSummary(selectedEvent)}
                      >
                        Print Summary
                      </Button>
                    </Box>

                    {selectedEvent.rsvps && Object.keys(selectedEvent.rsvps).length > 0 ? (
                      <>
                        {/* Summary Stats */}
                        {(() => {
                          const goingEntries = Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'going');
                          const totalAdults = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.adultsCount) || 1), 0);
                          const totalKids = goingEntries.reduce((sum, [_, r]) => sum + (parseInt(r.kidsCount) || 0), 0);
                          const hasGuestPricing = selectedEvent.budgetDetails?.guestPricing?.adultPrice;
                          const totalPaid = goingEntries.filter(([_, r]) => r.paid).length;
                          const totalDues = hasGuestPricing ? goingEntries.reduce((sum, [_, r]) =>
                            sum + calculateDues(r, selectedEvent.budgetDetails?.guestPricing), 0) : 0;
                          const paidDues = hasGuestPricing ? goingEntries.filter(([_, r]) => r.paid).reduce((sum, [_, r]) =>
                            sum + calculateDues(r, selectedEvent.budgetDetails?.guestPricing), 0) : 0;

                          return (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={4}>
                                  <Typography variant="caption" color="text.secondary">Total Guests</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {totalAdults + totalKids}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {totalAdults} adult{totalAdults !== 1 ? 's' : ''}, {totalKids} kid{totalKids !== 1 ? 's' : ''}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography variant="caption" color="text.secondary">RSVPs</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {goingEntries.length}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    going
                                  </Typography>
                                </Grid>
                                {hasGuestPricing && (
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Dues Collected</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: paidDues >= totalDues ? 'success.main' : 'warning.main' }}>
                                      ${paidDues.toFixed(0)} / ${totalDues.toFixed(0)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {totalPaid}/{goingEntries.length} paid
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          );
                        })()}

                        {/* Going List */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                          Going ({Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'going').length})
                        </Typography>
                        <List dense>
                          {Object.entries(selectedEvent.rsvps)
                            .filter(([_, rsvp]) => rsvp.status === 'going')
                            .map(([userId, rsvp]) => {
                              const hasGuestPricing = selectedEvent.budgetDetails?.guestPricing?.adultPrice;
                              const dues = hasGuestPricing ? calculateDues(rsvp, selectedEvent.budgetDetails?.guestPricing) : 0;
                              const isEventAuthor = selectedEvent.authorId === currentUser?.uid;

                              return (
                                <ListItem key={userId} sx={{ flexWrap: 'wrap' }}>
                                  <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                                      <PersonIcon fontSize="small" />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {rsvp.name}
                                        <Chip label="Going" size="small" color="success" sx={{ height: 18 }} />
                                        {rsvp.paid && <Chip label="Paid" size="small" color="info" sx={{ height: 18 }} icon={<PaidIcon />} />}
                                      </Box>
                                    }
                                    secondary={
                                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {((rsvp.adultsCount && rsvp.adultsCount > 0) || (rsvp.kidsCount && rsvp.kidsCount > 0)) && (
                                          <Typography variant="caption" component="span" color="text.secondary">
                                            {rsvp.adultsCount || 1} adult{(rsvp.adultsCount || 1) !== 1 ? 's' : ''}
                                            {rsvp.kidsCount > 0 && `, ${rsvp.kidsCount} kid${rsvp.kidsCount !== 1 ? 's' : ''}`}
                                          </Typography>
                                        )}
                                        {hasGuestPricing && dues > 0 && (
                                          <Typography variant="caption" component="span" color="text.secondary">
                                            Dues: ${dues.toFixed(2)}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                  {/* Admin: Toggle Payment */}
                                  {isEventAuthor && hasGuestPricing && (
                                    <Tooltip title={rsvp.paid ? 'Mark as unpaid' : 'Mark as paid'}>
                                      <Checkbox
                                        checked={!!rsvp.paid}
                                        onChange={() => handleTogglePayment(selectedEvent.id, userId)}
                                        size="small"
                                        color="success"
                                        icon={<MoneyIcon />}
                                        checkedIcon={<PaidIcon />}
                                      />
                                    </Tooltip>
                                  )}
                                </ListItem>
                              );
                            })}
                        </List>

                        {/* Maybe List */}
                        {Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'maybe').length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'warning.main' }}>
                              Maybe ({Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'maybe').length})
                            </Typography>
                            <List dense>
                              {Object.entries(selectedEvent.rsvps)
                                .filter(([_, rsvp]) => rsvp.status === 'maybe')
                                .map(([userId, rsvp]) => (
                                  <ListItem key={userId}>
                                    <ListItemAvatar>
                                      <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                                        <PersonIcon fontSize="small" />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={rsvp.name}
                                      secondary={<Chip label="Maybe" size="small" color="warning" sx={{ height: 18 }} />}
                                    />
                                  </ListItem>
                                ))}
                            </List>
                          </>
                        )}

                        {/* Not Going List */}
                        {Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'notGoing').length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'error.main' }}>
                              Can't Make It ({Object.entries(selectedEvent.rsvps).filter(([_, r]) => r.status === 'notGoing').length})
                            </Typography>
                            <List dense>
                              {Object.entries(selectedEvent.rsvps)
                                .filter(([_, rsvp]) => rsvp.status === 'notGoing')
                                .map(([userId, rsvp]) => (
                                  <ListItem key={userId}>
                                    <ListItemAvatar>
                                      <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32 }}>
                                        <PersonIcon fontSize="small" />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={rsvp.name}
                                      secondary={<Chip label="Can't Go" size="small" color="error" sx={{ height: 18 }} />}
                                    />
                                  </ListItem>
                                ))}
                            </List>
                          </>
                        )}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <PeopleIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No RSVPs yet
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Photos Tab */}
                {detailsTab === 2 && (
                  <Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <input
                        type="file"
                        ref={eventPhotoInputRef}
                        onChange={handleEventPhotoUpload}
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                      />
                      <Button
                        startIcon={photoUploading ? <CircularProgress size={16} /> : <PhotoIcon />}
                        onClick={() => eventPhotoInputRef.current?.click()}
                        disabled={photoUploading}
                        variant="contained"
                        size="small"
                      >
                        {photoUploading ? 'Uploading...' : 'Upload Photos'}
                      </Button>
                    </Box>

                    {loadingPhotos ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : eventPhotos.length > 0 ? (
                      <ImageList cols={3} gap={8}>
                        {eventPhotos.map((photo) => (
                          <ImageListItem key={photo.id} sx={{ position: 'relative' }}>
                            <img
                              src={sanitizeImageUrl(photo.url)}
                              alt={photo.fileName}
                              style={{ borderRadius: 8, width: '100%', height: 180, objectFit: 'cover' }}
                            />
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', p: 0.5, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="white" noWrap sx={{ flex: 1 }}>
                                {photo.uploadedByName}
                              </Typography>
                              {photo.uploadedBy === currentUser.uid && (
                                <IconButton size="small" onClick={() => handleDeleteEventPhoto(photo)} sx={{ color: 'white' }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <GalleryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No photos yet. Upload some memories from this event!
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDetailsDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* RSVP Guest Count Dialog */}
      <Dialog
        open={rsvpDialogOpen}
        onClose={() => setRsvpDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GoingIcon color="success" />
            RSVP — How many are coming?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Adults */}
            <TextField
              fullWidth
              label="Number of Adults"
              type="number"
              value={rsvpGuests.adultsCount}
              onChange={(e) => setRsvpGuests({ ...rsvpGuests, adultsCount: Math.max(1, parseInt(e.target.value) || 1) })}
              InputProps={{
                inputProps: { min: 1 },
                startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment>
              }}
            />

            {/* Kids */}
            <TextField
              fullWidth
              label="Number of Kids"
              type="number"
              value={rsvpGuests.kidsCount}
              onChange={(e) => {
                const count = Math.max(0, parseInt(e.target.value) || 0);
                const existing = rsvpGuests.kidsByAge || [];
                // Adjust kidsByAge array to match count
                let newKidsByAge = [...existing];
                while (newKidsByAge.length < count) {
                  newKidsByAge.push({ age: 0 });
                }
                if (newKidsByAge.length > count) {
                  newKidsByAge = newKidsByAge.slice(0, count);
                }
                setRsvpGuests({ ...rsvpGuests, kidsCount: count, kidsByAge: newKidsByAge });
              }}
              InputProps={{
                inputProps: { min: 0 },
                startAdornment: <InputAdornment position="start"><ChildCareIcon fontSize="small" /></InputAdornment>
              }}
            />

            {/* Kids Age Inputs (for pricing tier calculation) */}
            {rsvpGuests.kidsCount > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Enter each child's age (for pricing):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(rsvpGuests.kidsByAge || []).map((kid, idx) => (
                    <TextField
                      key={idx}
                      size="small"
                      label={`Child ${idx + 1} age`}
                      type="number"
                      value={kid.age}
                      onChange={(e) => {
                        const newKids = [...rsvpGuests.kidsByAge];
                        newKids[idx] = { age: Math.max(0, parseInt(e.target.value) || 0) };
                        setRsvpGuests({ ...rsvpGuests, kidsByAge: newKids });
                      }}
                      InputProps={{ inputProps: { min: 0, max: 17 } }}
                      sx={{ width: 110 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Show estimated dues if guest pricing is set */}
            {(() => {
              const event = events.find(e => e.id === rsvpEventId);
              const guestPricing = event?.budgetDetails?.guestPricing;
              if (!guestPricing?.adultPrice) return null;
              const estimatedDues = calculateDues(rsvpGuests, guestPricing);
              return (
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon fontSize="small" /> Estimated Dues
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rsvpGuests.adultsCount} adult{rsvpGuests.adultsCount !== 1 ? 's' : ''} x ${parseFloat(guestPricing.adultPrice).toFixed(2)}
                    {rsvpGuests.kidsCount > 0 && ` + ${rsvpGuests.kidsCount} kid${rsvpGuests.kidsCount !== 1 ? 's' : ''}`}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                    Total: ${estimatedDues.toFixed(2)}
                  </Typography>
                </Paper>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRsvpDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRSVPSubmitWithGuests}
            variant="contained"
            color="success"
            startIcon={<GoingIcon />}
          >
            Confirm RSVP
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Events;
